import { Link } from "@mui/material";
import type {
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRowParams,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import React, { type JSX, useContext, useEffect, useState } from "react";
import { PatientContext } from "../../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../../contexts/ProjectContext";
import AccordionTable from "../../datagrid/AccordionTable.tsx";
import {
  getAttributeValue,
  getCategoryByType,
  getCategoryIcon,
  getCategoryName,
  getOtherData,
} from "./CategorizeUtils";

type PatientAttributeData = {
  id: string;
  icon?: JSX.Element;
  category: string;
  value: string;
  otherData: string[];
  modifiedAt: string;
};

export default function PatientAttributeDataGrid({
  loading = false,
  title,
  subtitle,
  handleRowEdit,
}: {
  loading?: boolean;
  title: string;
  subtitle: string;
  handleRowEdit: (
    params: GridCellParams | GridRowParams,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
  ) => void;
}) {
  const [data, setData] = useState<PatientAttributeData[]>([]);
  const { imdrfEnabled, attributeTypes } = useContext(ProjectContext)!;
  const { attributes, highlightedAttributesCount } =
    useContext(PatientContext)!;

  const renderValueCell = (
    param: GridRenderCellParams<PatientAttributeData>,
  ) => {
    return (
      <Link
        component="button"
        variant="subtitle2"
        style={{ textAlign: "left" }}
      >
        {param.value}
      </Link>
    );
  };

  const handleCellClick: GridEventListener<"cellClick"> = (params, event) => {
    if (params.field === "value") {
      handleRowEdit(params, event);
    }
  };

  const fields: GridColDef<PatientAttributeData>[] = [
    {
      field: "categoryIcon",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 50,
      align: "center",
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.row.icon;
      },
    },
    {
      field: "category",
      headerName: "Category",
      width: 250,
    },
    {
      field: "value",
      headerName: "Value",
      width: 275,
      filterable: false,
      renderCell: renderValueCell,
    },
    {
      field: "otherData",
      headerName: "Other Data",
      sortable: false,
      flex: 1,
      minWidth: 250,
      filterable: false,
      renderCell: (params) => {
        return (
          <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
            {params.value.map((fieldData: string, index: number) => (
              <li key={index}>{fieldData}</li>
            ))}
          </ul>
        );
      },
    },
  ];

  useEffect(() => {
    const attributeData: PatientAttributeData[] = [];
    if (attributeTypes && attributes && attributes.length > 0) {
      attributes.forEach((a) => {
        const categoryName = getCategoryName(attributeTypes, a);
        const patientAttributeType = getCategoryByType(
          attributeTypes,
          a.attributeTypeId,
        );
        const attribute = {
          id: a.id,
          icon: patientAttributeType
            ? getCategoryIcon(patientAttributeType)
            : undefined,
          category: categoryName,
          value: getAttributeValue(imdrfEnabled ?? false, attributeTypes, a),
          otherData: getOtherData(imdrfEnabled ?? false, attributeTypes, a),
          modifiedAt: a.modifiedAt,
        };

        attributeData.push(attribute);
      });
    }
    setData(attributeData);
  }, [attributes, attributeTypes, imdrfEnabled]);

  return (
    <AccordionTable
      title={title}
      titleClass={"categorization-row-title-font"}
      subtitle={subtitle}
      subtitleClass={"categorization-row-subtitle-font"}
      loading={loading}
      columns={fields}
      rows={data}
      startExpanded={data.length > 0}
      highlightField={"modifiedAt"}
      highlightCount={highlightedAttributesCount}
      totalCount={data.length}
      initialState={{
        sorting: {
          sortModel: [{ field: "category", sort: "asc" }],
        },
      }}
      onRowDoubleClick={handleRowEdit}
      onCellClick={handleCellClick}
      disableRowGrouping
      tableId="patient-attributes-table"
    />
  );
}
